import { useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  CreateEnvironmentInput,
  Environment,
  UpdateEnvironmentInput,
} from './environments-types';

// Mirrors apps/api/src/admin/environments/dto/environment.dto.ts. Code regex
// matches the platform code style for consistency in dropdowns and YAML.
const CODE_REGEX = /^[a-z][a-z0-9_]*$/;

const schema = z.object({
  code: z
    .string()
    .min(2)
    .max(32)
    .regex(CODE_REGEX, 'Lowercase letters, digits, and underscores; must start with a letter'),
  name: z.string().min(1).max(64),
  is_active: z.boolean().default(true),
  sort_order: z.number().int().default(0),
});
type FormData = z.infer<typeof schema>;

interface EnvironmentsFormProps {
  initial?: Environment;
  onSubmit: (data: CreateEnvironmentInput | UpdateEnvironmentInput) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function EnvironmentsForm({
  initial,
  onSubmit,
  onCancel,
  isSubmitting,
}: EnvironmentsFormProps): JSX.Element {
  const isEdit = !!initial;
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema) as unknown as Resolver<FormData>,
    defaultValues: initial
      ? {
          code: initial.code,
          name: initial.name,
          is_active: initial.is_active,
          sort_order: initial.sort_order,
        }
      : { code: '', name: '', is_active: true, sort_order: 0 },
  });

  useEffect(() => {
    if (initial) {
      reset({
        code: initial.code,
        name: initial.name,
        is_active: initial.is_active,
        sort_order: initial.sort_order,
      });
    }
  }, [initial, reset]);

  const isActive = watch('is_active');

  function handleFormSubmit(data: FormData): void {
    if (isEdit) {
      // RT#1: strip `code` from PATCH payload (immutable, would 400).
      const { code: _stripped, ...patch } = data;
      void onSubmit(patch as UpdateEnvironmentInput);
    } else {
      void onSubmit({
        code: data.code,
        name: data.name,
        is_active: data.is_active,
        sort_order: data.sort_order,
      } as CreateEnvironmentInput);
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="code">Code</Label>
        <Input
          id="code"
          placeholder="production"
          {...register('code')}
          disabled={isEdit}
        />
        {isEdit && (
          <p className="text-xs text-muted-foreground">
            Code is locked after creation. To rename the identifier, delete and recreate.
          </p>
        )}
        {!isEdit && errors.code && (
          <p className="text-sm text-destructive">{errors.code.message as string}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="name">Display Name</Label>
        <Input id="name" placeholder="Production" {...register('name')} />
        {errors.name && (
          <p className="text-sm text-destructive">{errors.name.message as string}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="sort_order">Sort Order</Label>
        <Input
          id="sort_order"
          type="number"
          {...register('sort_order', { valueAsNumber: true })}
        />
        <p className="text-xs text-muted-foreground">
          Controls the order this environment appears in dropdowns (ascending).
        </p>
        {errors.sort_order && (
          <p className="text-sm text-destructive">{errors.sort_order.message as string}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Switch
          id="is_active"
          checked={isActive}
          onCheckedChange={(c) => setValue('is_active', c)}
        />
        <Label htmlFor="is_active">Active</Label>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
